import React from 'react';
import { Formik, Form } from 'formik';

import {
  LISTING_WITHDRAWAL_REASON_OTHER,
  LISTING_WITHDRAWAL_REASON_LEASED,
  LISTING_SUNSETUP_WITHDRAWAL_REASON_OPTION_ITEMS,
} from 'common/listingConstants';

import {
  showFieldIf,
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import SrFormikField, {
  SR_FIELD_THEME_PARTNER_FORM_ADOBE,
  SR_FIELD_THEME_FORM_INPUT_BORDER_2X,
} from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrTextGreycliff from 'client/components/SrTextGreycliff';
import SrPartnerListingWithdrawModal from 'client/components/SrPartnerListingWithdrawCard/SrPartnerListingWithdrawModal';

const getFormFields = ({ formValues = {} } = {}) => {
  const formFields = [
    {
      id: 'withdrawalReason',
      type: 'select',
      properties: {
        placeholder: 'Choose uninstall reason',
        enableResetOption: true,
        choices: LISTING_SUNSETUP_WITHDRAWAL_REASON_OPTION_ITEMS,
      },
      validations: ['required'],
    },
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_OTHER, {
      id: 'withdrawalReasonOther',
      title: 'Why are you ordering an withdrawal?',
      properties: {
        placeholder: 'Write withdrawal reason here...',
      },
      type: 'long_text',
      validations: ['required'],
    }),
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED, {
      id: 'commissionAmount',
      title: 'Commission Amount',
      properties: {
        placeholder: '$',
        className: SR_FIELD_THEME_FORM_INPUT_BORDER_2X,
      },
      type: 'currency',
      validations: ['required'],
      className: 'adobeShortLabel',
    }),
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED, {
      id: 'rentPrice',
      title: 'Rent Price',
      properties: {
        placeholder: '$',
        className: SR_FIELD_THEME_FORM_INPUT_BORDER_2X,
      },
      type: 'currency',
      validations: ['required'],
      className: 'adobeShortLabel',
    }),
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED, {
      id: 'tenantNames',
      title: 'Tenant names',
      properties: {
        placeholder: 'Name',
        className: SR_FIELD_THEME_FORM_INPUT_BORDER_2X,
      },
      type: 'short_text',
      validations: ['required'],
      className: 'adobeShortLabel',
    }),
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED, {
      id: 'moveInDate',
      title: 'Move-In Date',
      properties: {
        placeholder: 'Select date',
        className: `${SR_FIELD_THEME_FORM_INPUT_BORDER_2X} uninstallOrderDatePicker`,
      },
      type: 'date_picker',
      validations: ['required'],
      className: 'adobeShortLabel',
    }),
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED, {
      id: 'leasingAgent',
      title: 'Leasing Agent',
      properties: {
        placeholder: 'Name',
        className: SR_FIELD_THEME_FORM_INPUT_BORDER_2X,
      },
      type: 'short_text',
      validations: ['required'],
      className: 'adobeShortLabel',
    }),
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED, {
      id: 'listingAgent',
      title: 'Listing Agent',
      properties: {
        placeholder: 'Name',
        className: SR_FIELD_THEME_FORM_INPUT_BORDER_2X,
      },
      type: 'short_text',
      validations: ['required'],
      className: 'adobeShortLabel',
    }),
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED, {
      id: 'coListingAgentName',
      title: 'Co-Broker / Agent',
      properties: {
        placeholder: 'Name',
        className: SR_FIELD_THEME_FORM_INPUT_BORDER_2X,
      },
      type: 'short_text',
      className: 'adobeShortLabel',
    }),
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED, {
      id: 'notes',
      title: 'Notes',
      properties: {
        placeholder: 'Write here...',
      },
      type: 'long_text',
      validations: ['required'],
    }),
    showFieldIf(formValues.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED, {
      id: 'subMlsFormPdfUrl',
      title: 'Upload HR Sub MLS Form',
      properties: {
        fileAccept: '*',
        filePath: 'listing/sub-mls-form/{uuid}',
      },
      type: 'file_single',
      validations: ['required'],
      className: 'adobeShortLabel',
    }),
  ];

  return formFields;
};

const SrPartnerListingWithdrawReasonModal = ({
  partner,
  onClose,
  onSubmit,
}) => {
  return (
    <Formik
      initialValues={createInitialValues({
        formFields: getFormFields(),
        defaultValues: {
          withdrawalReason: 'Leased',
          leasingAgent: 'Ben Doherty',
          listingAgent: 'Ben Doherty',
        },
      })}
      validate={(formValues) => {
        const formFields = getFormFields({ formValues });
        return createFormValidationFn(formFields)(formValues);
      }}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({ formValues: formikBag.values });
        return (
          <SrPartnerListingWithdrawModal
            disabled={!formikBag.isValid}
            onClose={onClose}
            onConfirm={formikBag.handleSubmit}
          >
            <Form>
              <SrBox textAlign='center' margin='0 0 20px'>
                <img
                  width='130px'
                  height='auto'
                  src='/images/install-marketing-items-car.svg'
                  alt='uninstall'
                />
              </SrBox>
              <SrTextGreycliff weight='600' lineHeight='25px'>
                Choose why you are withdrawing this listing,
                and optionally request the removal of marketing items.
              </SrTextGreycliff>
              <SrBox
                marginTop='10px'
                maxHeight='400px'
                overflowY='scroll'
                overflowX='hidden'
              >
                {formFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                  />
                ))}
                {formikBag.values.withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED && (
                  <SrTextGreycliff weight='500' lineHeight='18px' fontSize='12px'>
                    This information is used to generate the invoice,
                    and will send the email to {partner.billingEmail}
                  </SrTextGreycliff>
                )}
              </SrBox>
            </Form>
          </SrPartnerListingWithdrawModal>
        );
      }}
    />
  );
};

SrPartnerListingWithdrawReasonModal.displayName = 'SrPartnerListingWithdrawReasonModal';

export default SrPartnerListingWithdrawReasonModal;
